/**
 * WorkspaceAdmin - 슈퍼어드민용 워크스페이스 확장 타입들
 * 
 * 이 파일은 슈퍼어드민 관리 화면에서 사용하는 추가 타입을 정의합니다.
 * 기존 Workspace 클래스와 함께 사용됩니다.
 */

import { WorkspaceTier } from './local/Types';

// ===== 회사정보 (사용자 입력, 선택적) =====
export interface CompanyInfo {
    companyName: string;
    representative?: string;
    department?: string;           // 일본 비즈니스 필수
    email: string;
    phone?: string;
    postalCode?: string;
    address?: string;
    registrationNumber?: string;   // 사업자등록번호
    updatedAt?: Date;
    updatedBy?: string;            // 수정자 UID
}

// ===== 청구정보 (슈퍼어드민 관리, 유료 계약 시) =====
export interface BillingInfo {
    // null/undefined면 companyInfo 사용
    companyName?: string;
    representative?: string;
    department?: string;
    email: string;
    postalCode?: string;
    address?: string;

    // 일본 비즈니스 필수 필드
    jp_registrationNumber?: string;  // 적격청구서 등록번호 (T + 13자리)
    jp_bankInfo?: string;            // 입금 계좌 정보

    country: 'JP' | 'KR' | 'US';
    updatedAt?: Date;
    updatedBy?: string;
}

// ===== 영업정보 (슈퍼어드민 관리) =====
export type SalesStatus = 'free' | 'lead' | 'negotiating' | 'contracted';

export interface SalesInfo {
    status: SalesStatus;
    assignedTo?: string;           // 담당 영업 UID
    notes?: string;                // 영업 메모
    lastContactAt?: Date;          // 마지막 연락일
    nextFollowUp?: Date;           // 다음 연락 예정일
}

// ===== Overview 캐시 (Cloud Functions 자동 관리) =====
export interface WorkspaceOverview {
    // Subscription 요약
    currentSubscription: {
        id: string;
        planId: WorkspaceTier;
        status: 'active' | 'past_due' | 'canceled' | 'expired';
        amount: number;
        currency: string;
        startDate: Date;
        endDate: Date | null;
        discount?: {
            type: 'percent' | 'fixed';
            value: number;
            reason?: string;
        };
        reason?: string;           // 변경 사유
    } | null;

    scheduledSubscription: {
        id: string;
        planId: WorkspaceTier;
        scheduledFor: Date;
        amount: number;
    } | null;

    subscriptionCount: number;

    // Documents 요약
    latestInvoice: {
        id: string;
        invoiceNumber: string;
        amount: number;
        status: 'draft' | 'issued' | 'paid' | 'canceled';
        issuedAt: Date;
    } | null;

    unpaidAmount: number;          // 미수금 총액
    invoiceCount: number;
    receiptCount: number;

    // 문의 요약
    inquiryCount: number;
    latestInquiry?: {
        id: string;
        type: string;
        status: string;
        createdAt: Date;
    };

    // Usage 요약
    usage: {
        storageBytes: number;
        monthlyDownloadBytes: number;
        memberCount: number;
        channelCount: number;
        botCount: number;
        storageCount: number;
        lastUpdated: Date;
    };

    lastUpdated: Date;
}

// ===== 기본값 생성 함수들 =====

export const defaultCompanyInfo = (): CompanyInfo => ({
    companyName: '',
    email: '',
});

export const defaultBillingInfo = (): BillingInfo => ({
    email: '',
    country: 'JP',
});

export const defaultSalesInfo = (): SalesInfo => ({
    status: 'free',
});

export const defaultWorkspaceOverview = (): WorkspaceOverview => ({
    currentSubscription: null,
    scheduledSubscription: null,
    subscriptionCount: 0,
    latestInvoice: null,
    unpaidAmount: 0,
    invoiceCount: 0,
    receiptCount: 0,
    inquiryCount: 0,
    usage: {
        storageBytes: 0,
        monthlyDownloadBytes: 0,
        memberCount: 0,
        channelCount: 0,
        botCount: 0,
        storageCount: 0,
        lastUpdated: new Date(),
    },
    lastUpdated: new Date(),
});
